/**
 * Device Registry — global catalog of all devices (instances, not profiles)
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Database, Search, Cpu, MapPin, Hash, ChevronRight } from "lucide-react";
import { getMockProfiles, STATUS_TONE, STATUS_LABEL } from "@/lib/device/profiles";

export const Route = createFileRoute("/integration/device-registry")({
  component: DeviceRegistry,
});

function DeviceRegistry() {
  const [search, setSearch] = useState("");

  const q = useQuery({
    queryKey: ["device-registry"],
    queryFn: async () => {
      await new Promise((r) => setTimeout(r, 150));
      // Each profile × 3 instances on average
      const profiles = getMockProfiles();
      return profiles.flatMap((p, i) =>
        Array.from({ length: 2 + (i % 3) }).map((_, j) => ({
          instanceId: `${p.id}-inst-${j}`,
          serialNumber: `SN${(1000000 + i * 1000 + j).toString()}`,
          profileId: p.id,
          vendor: p.vendor,
          model: p.model,
          location: ["ICU", "Surgery", "ER", "Lab", "Ward 3A", "Ward 2B", "Cardiology"][j % 7],
          status: p.status,
          lastSeen: p.lastHeartbeat,
        }))
      );
    },
  });

  const filtered = useMemo(() => {
    const data = q.data ?? [];
    if (!search) return data;
    const s = search.toLowerCase();
    return data.filter((d) =>
      d.serialNumber.toLowerCase().includes(s) ||
      d.vendor.toLowerCase().includes(s) ||
      d.model.toLowerCase().includes(s) ||
      d.location.toLowerCase().includes(s)
    );
  }, [q.data, search]);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Database className="h-5 w-5" /> Device Registry
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Inventaire de tous les dispositifs physiques (instances)
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Rechercher (SN, vendor, model, lieu)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
        {q.isLoading ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16" />)
        ) : (
          filtered.map((d) => (
            <Card key={d.instanceId}>
              <CardContent className="flex items-center gap-3 p-3">
                <Cpu className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{d.vendor} {d.model}</span>
                    <Badge variant={STATUS_TONE[d.status as keyof typeof STATUS_TONE]} className="text-[10px]">
                      {STATUS_LABEL[d.status as keyof typeof STATUS_LABEL]}
                    </Badge>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Hash className="h-3 w-3" /> {d.serialNumber}
                    <span>·</span>
                    <MapPin className="h-3 w-3" /> {d.location}
                  </div>
                </div>
                <Button variant="ghost" size="icon" asChild>
                  <Link to="/integration/profiles"><ChevronRight className="h-3.5 w-3.5" /></Link>
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {!q.isLoading && filtered.length === 0 && (
        <div className="rounded-lg border border-dashed border-white/10 p-8 text-center text-sm text-muted-foreground">
          Aucun dispositif
        </div>
      )}
    </div>
  );
}
